import {
  AnimatedSpinner,
  Body,
  Divider,
  Heading,
  IconStar,
  LayoutVerticalSpacer,
  Pagination,
  formatRatingScore,
  useMediumFromMediaQuery,
} from '@madpaws/design-system';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import useConstant from 'use-constant';

import { SitterApi } from '~/services/sitter/SitterApi';

import styles from './CustomerReviews.module.css';
import { DotDivider } from './DotDivider/DotDivider';
import { Review } from './Review/Review';
import { CUSTOMER_REVIEWS_ID } from '../components/ReviewsLink/constants';

import type { LinkComponent } from '@madpaws/design-system';
import type { ReactElement } from 'react';
import type { SitterProfileCard, SitterReview } from '~/common/types/sitter';

type CustomerReviewsProps = {
  canonicalUrl: string | null;
  sitterCard: SitterProfileCard;
};

// TODO (WEB-1549) Remove after Design System update
Link.defaultProps = {
  legacyBehavior: true,
};

const truncateUrl = (url: string): string => {
  // Find the position of the first occurrence of '/' after 'https://'
  const startIndex = url.indexOf('/', 'https://'.length);
  // Extract the substring starting from that position
  const truncatedUrl = url.substring(startIndex);
  return truncatedUrl;
};

export const CustomerReviews = ({
  canonicalUrl,
  sitterCard,
}: CustomerReviewsProps): ReactElement | null => {
  const { firstName, rating, totalReviews, sitterId } = sitterCard;
  const isMediumFromViewport = useMediumFromMediaQuery();
  const reviewsPerPage = 10;

  const [currentOffset, setCurrentOffset] = useState(0);
  const [currentSize, setCurrentSize] = useState(0);
  const [currentTotal, setCurrentTotal] = useState(0);
  const [reviewsToDisplay, setReviewsToDisplay] = useState<SitterReview[]>([]);
  const [hasPagination, setHasPagination] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const truncatedUrl = truncateUrl(canonicalUrl ?? '');

  const getReviews = (page = 1): void => {
    setReviewsLoading(true);
    SitterApi.getSitterReviews(sitterId, reviewsPerPage, page)
      .then((response) => {
        const { currentPage, perPage, total } = response.meta.pagination;
        const offset = (currentPage - 1) * perPage;
        const size = perPage;

        setReviewsToDisplay(response.data);
        setCurrentOffset(offset);
        setCurrentSize(size);
        setCurrentTotal(total);
        setHasPagination(total > size);
      })
      .catch(() => {
        setReviewsToDisplay([]);
        setCurrentOffset(0);
        setCurrentSize(0);
        setCurrentTotal(0);
        setHasPagination(false);
      })
      .finally(() => {
        setReviewsLoading(false);
      });
  };

  useEffect(() => {
    getReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // define a single instance of debounced function per component
  const handleButtonClick = useConstant(() =>
    AwesomeDebouncePromise((page: number): void => {
      getReviews(page);
    }, 400)
  );

  return (
    <div className={styles.root} id={CUSTOMER_REVIEWS_ID}>
      <LayoutVerticalSpacer space={isMediumFromViewport ? 'large2x' : 'large1x'}>
        <Heading size={isMediumFromViewport ? 'small2x' : 'small3x'}>
          <div className={styles.averageRatingAndTotalReviews}>
            <IconStar />
            <div className={styles.averageRating}>{formatRatingScore(rating)}</div>
            <DotDivider />
            <div>{totalReviews} ratings</div>
          </div>
        </Heading>
      </LayoutVerticalSpacer>

      <LayoutVerticalSpacer space={isMediumFromViewport ? 'large2x' : 'large1x'}>
        {reviewsLoading ? (
          <div className={styles.loading}>
            <AnimatedSpinner />
          </div>
        ) : reviewsToDisplay && reviewsToDisplay.length > 0 ? (
          <div>
            <ul role="list">
              {reviewsToDisplay.map((review, index) => (
                <li key={index}>
                  <LayoutVerticalSpacer space={isMediumFromViewport ? 'large2x' : 'large1x'}>
                    <Review review={review} />
                    {index !== reviewsToDisplay.length - 1 ? <Divider /> : <></>}
                  </LayoutVerticalSpacer>
                </li>
              ))}
            </ul>
            {hasPagination && (
              <Pagination
                currentOffset={currentOffset}
                isClientSide
                label="reviews"
                linkComponent={Link as LinkComponent}
                onPageChange={handleButtonClick}
                pageHrefTemplate={truncatedUrl}
                pageSize={currentSize}
                totalItems={currentTotal}
              />
            )}
          </div>
        ) : (
          <div className={styles.noReview}>
            <Body>{firstName} doesn&apos;t have any reviews yet.</Body>
          </div>
        )}
      </LayoutVerticalSpacer>

      <div className={styles.divider}>
        <Divider />
      </div>
    </div>
  );
};
